import { useState, useEffect } from 'react';
import {
  Heading,
  BodyText,
  MetricCard,
  Card,
  Badge,
  TableScroll,
} from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/generated/cooperative-reserve-token';
import { cooperativeReserveTokenClient } from '../../api/typed-clients';
// Side-effect import to ensure auth middleware is registered on the client.
import '../../api/client';

type NetworkMetrics = components['schemas']['NetworkMetrics'];
type ParticipantPosition = components['schemas']['ParticipantPosition'];
type ReserveReconciliation = components['schemas']['ReserveReconciliation'];
type ReserveAttestation = components['schemas']['ReserveAttestation'];

export function SyndicationCrt() {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<NetworkMetrics | null>(null);
  const [reconciliation, setReconciliation] = useState<ReserveReconciliation | null>(null);
  const [attestations, setAttestations] = useState<ReserveAttestation[]>([]);
  const [positions, setPositions] = useState<ParticipantPosition[]>([]);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [metricsRes, reconRes, attestRes, posRes] = await Promise.all([
          cooperativeReserveTokenClient.GET('/api/v1/crt/network/metrics'),
          cooperativeReserveTokenClient.GET('/api/v1/crt/reserve/reconciliation'),
          cooperativeReserveTokenClient.GET('/api/v1/crt/reserve/attestations'),
          cooperativeReserveTokenClient.GET('/api/v1/crt/positions'),
        ]);
        if (metricsRes.data) setMetrics(metricsRes.data);
        if (reconRes.data) setReconciliation(reconRes.data);
        if (attestRes.data) setAttestations(attestRes.data.attestations ?? []);
        if (posRes.data) setPositions(posRes.data.positions ?? []);
        if (metricsRes.error && reconRes.error && attestRes.error && posRes.error) {
          setServiceUnavailable(true);
        }
      } catch {
        // Backend not yet deployed (port 9571).
        setServiceUnavailable(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact' }).format(amount);

  const formatCurrencyFull = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const formatPercent = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: 1 }).format(value);

  const attestationLabel = (type: ReserveAttestation['type']) => {
    switch (type) {
      case 'MINT': return 'Mint';
      case 'BURN': return 'Burn';
      case 'INTEREST_MINT': return 'Interest Mint';
      case 'RECONCILIATION': return 'Reconciliation';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center mt-8">
        <BodyText variant="ui">Loading CRT data...</BodyText>
      </div>
    );
  }

  if (serviceUnavailable) {
    return (
      <div>
        <div className="mb-6">
          <BodyText variant="ui" className="text-aaim-stone-600">
            CRT lifecycle management -- minting, burning, transfers, and reserve attestations
          </BodyText>
        </div>
        <Card className="p-8 text-center">
          <Heading level={2} size="card" className="font-sans mb-2">
            Service Not Yet Available
          </Heading>
          <BodyText variant="ui" className="text-aaim-stone-600 max-w-lg mx-auto">
            The Cooperative Reserve Token backend service (port 9571) has not
            been deployed. The OpenAPI specification and typed clients are in
            place; the service itself is on the deployment roadmap.
          </BodyText>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <BodyText variant="ui" className="text-aaim-stone-600">
            CRT lifecycle management -- minting, burning, transfers, and reserve attestations
          </BodyText>
        </div>
        {/*
          "Request Reconciliation" Save-button affordance removed (Sprint C11
          Lane 5): there was no onClick, no mutation, no backend route. Per
          Save Button Discipline rule (codebot/.claude/rules/ui-package.md),
          an unwired mutation button is forbidden. Restore once the
          cooperative-reserve-token backend ships
          POST /api/v1/crt/reserve/reconciliation/request.
        */}
      </div>

      {/* Reserve Parity Status */}
      <Card className="mb-6 p-4">
        <div className="flex items-center justify-between">
          <div>
            <BodyText variant="ui" className="font-semibold">Reserve Parity</BodyText>
            <BodyText variant="ui" className="text-aaim-stone-500">
              {formatCurrency(reconciliation?.totalFiatCustody ?? 0)} fiat backing{' '}
              {formatCurrency(reconciliation?.totalCrtSupply ?? 0)} CRT
              {reconciliation?.parityRatio != null && ` (${reconciliation.parityRatio.toFixed(4)} ratio)`}
            </BodyText>
          </div>
          <Badge variant="neutral">
            {reconciliation?.status === 'BALANCED' ? '1:1 Parity Maintained' : reconciliation?.status ?? 'Unknown'}
          </Badge>
        </div>
      </Card>

      {/* Network Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard
          label="Total CRT Supply"
          value={formatCurrency(metrics?.totalSupply ?? 0)}
          description="1:1 USD parity"
        />
        <MetricCard
          label="Active Participants"
          value={String(metrics?.activeParticipants ?? 0)}
          description={`${metrics?.totalParticipants ?? 0} registered`}
        />
        <MetricCard
          label="Pool Utilization"
          value={formatPercent(metrics?.poolUtilization ?? 0)}
          description="CRT deployed in participations"
        />
        <MetricCard
          label="Fiat in Custody"
          value={formatCurrency(metrics?.totalFiatCustody ?? 0)}
          description="Trust Company"
        />
      </div>

      {/* Reserve Attestations */}
      <Card className="mb-6 p-4">
        <Heading level={2} size="label" className="font-sans mb-4">Reserve Attestations</Heading>
        {attestations.length === 0 ? (
          <BodyText variant="ui" className="text-aaim-stone-500">No attestations available</BodyText>
        ) : (
          <div className="space-y-3">
            {attestations.map((att) => (
              <div key={att.attestationId} className="flex items-center justify-between py-2 border-b border-aaim-stone-200 last:border-0">
                <div className="flex items-center gap-3">
                  <Badge variant="neutral">{attestationLabel(att.type)}</Badge>
                  <div>
                    <BodyText variant="ui" className="font-medium">
                      {att.attestationId.slice(0, 8)}...
                    </BodyText>
                    <BodyText variant="ui" className="text-aaim-stone-500 text-sm">
                      {att.wireReference ?? 'No wire reference'}
                    </BodyText>
                  </div>
                </div>
                <div className="text-right">
                  <BodyText variant="ui" className="font-medium">{formatCurrencyFull(att.amount)}</BodyText>
                  <BodyText variant="ui" className="text-aaim-stone-500 text-sm">
                    {new Date(att.issuedAt).toLocaleDateString()}
                  </BodyText>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Participant Positions */}
      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-aaim-stone-200">
          <Heading level={2} size="label" className="font-sans">Participant Positions</Heading>
        </div>
        {positions.length === 0 ? (
          <div className="p-4">
            <BodyText variant="ui" className="text-aaim-stone-500">No positions available</BodyText>
          </div>
        ) : (
          <TableScroll label="Participant positions">
          <table className="w-full min-w-table-wide text-sm">
            <thead>
              <tr className="border-b border-aaim-stone-200 bg-aaim-stone-50">
                <th className="px-4 py-3 text-left font-medium text-aaim-stone-600">Institution</th>
                <th className="px-4 py-3 text-left font-medium text-aaim-stone-600">Total Balance</th>
                <th className="px-4 py-3 text-left font-medium text-aaim-stone-600">Available</th>
                <th className="px-4 py-3 text-left font-medium text-aaim-stone-600">Locked</th>
                <th className="px-4 py-3 text-left font-medium text-aaim-stone-600">Participations</th>
                <th className="px-4 py-3 text-left font-medium text-aaim-stone-600">Interest Earned</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((pos) => (
                <tr key={pos.participantId} className="border-b border-aaim-stone-100 hover:bg-aaim-stone-50">
                  <td className="px-4 py-3">{pos.institutionName ?? pos.participantId.slice(0, 8)}</td>
                  <td className="px-4 py-3">{formatCurrencyFull(pos.totalBalance)}</td>
                  <td className="px-4 py-3">{formatCurrencyFull(pos.availableBalance)}</td>
                  <td className="px-4 py-3">{formatCurrencyFull(pos.lockedBalance)}</td>
                  <td className="px-4 py-3">{pos.activeParticipations ?? 0}</td>
                  <td className="px-4 py-3">{formatCurrencyFull(pos.totalInterestEarned ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableScroll>
        )}
      </Card>
    </div>
  );
}
